import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './Announcements.css';
import axios from 'axios';

const PLAYABLE_DEVICE_TYPES = new Set(['TV', 'Music Player']);

const LANGUAGE_LABELS = {
  'en-AU': 'English - Australia',
  'en-GB': 'English - Great Britain',
  'en-US': 'English - United States',
  'en-CA': 'English - Canada',
  'en-IN': 'English - India',
  'vi-VN': 'Vietnamese - Vietnam',
};

function getLanguageLabel(code) {
  return LANGUAGE_LABELS[code] || code || 'Unknown';
}

function getVoiceDisplayName(voiceName = '', languageCode = '') {
  if (languageCode && voiceName.startsWith(`${languageCode}-`)) {
    return voiceName.replace(`${languageCode}-`, '');
  }
  return voiceName || 'Unknown';
}

function Announcements({ selectedDevice, devices = [] }) {
  const token = localStorage.getItem('token');
  const [announcements, setAnnouncements] = useState([]);
  const [selectedAnnouncementId, setSelectedAnnouncementId] = useState(null);
  const [playTargetDeviceId, setPlayTargetDeviceId] = useState('');
  const [playNowBusy, setPlayNowBusy] = useState(false);
  const [playNowStatus, setPlayNowStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const playableDevices = useMemo(
    () => (Array.isArray(devices) ? devices : []).filter((device) => PLAYABLE_DEVICE_TYPES.has(device?.type)),
    [devices]
  );

  const selectedAnnouncement = useMemo(
    () => announcements.find((item) => String(item.id) === String(selectedAnnouncementId || '')) || null,
    [announcements, selectedAnnouncementId]
  );

  const refreshAnnouncements = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/announcements', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const list = Array.isArray(response.data) ? response.data : [];
      setAnnouncements(list);
      setSelectedAnnouncementId((current) => {
        if (list.some((item) => String(item.id) === String(current || ''))) return current;
        return list[0]?.id || null;
      });
      return list;
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    refreshAnnouncements().catch((error) => {
      console.error('Error fetching campaign announcements:', error);
    });
  }, [refreshAnnouncements]);

  useEffect(() => {
    const selectedDeviceId = selectedDevice?.id ? String(selectedDevice.id) : '';
    const selectedDevicePlayable = selectedDevice && PLAYABLE_DEVICE_TYPES.has(selectedDevice.type);
    if (selectedDevicePlayable && playableDevices.some((device) => String(device.id) === selectedDeviceId)) {
      setPlayTargetDeviceId(selectedDeviceId);
      return;
    }
    if (playableDevices.length === 0) {
      setPlayTargetDeviceId('');
      return;
    }
    if (playableDevices.some((device) => String(device.id) === String(playTargetDeviceId || ''))) {
      return;
    }
    setPlayTargetDeviceId(String(playableDevices[0].id));
  }, [playTargetDeviceId, playableDevices, selectedDevice]);

  const handlePlayAnnouncementNow = async () => {
    if (!selectedAnnouncement?.id) {
      alert('Select a campaign announcement first.');
      return;
    }
    if (!playTargetDeviceId) {
      alert('Select a TV or Music Player device first.');
      return;
    }

    setPlayNowBusy(true);
    setPlayNowStatus(null);
    try {
      const response = await axios.post(
        `/api/announcements/${selectedAnnouncement.id}/play`,
        { deviceId: Number(playTargetDeviceId) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPlayNowStatus({
        type: 'success',
        message: response.data?.message || 'Announcement triggered successfully.',
      });
    } catch (error) {
      console.error('Error triggering announcement playback:', error);
      setPlayNowStatus({
        type: 'error',
        message:
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          'Failed to trigger announcement playback.',
      });
    } finally {
      setPlayNowBusy(false);
    }
  };

  return (
    <div className="announcements-section">
      <h3>Campaign Announcements</h3>
      <p className="announcements-intro">
        These announcements are saved from campaigns in Creative Studio. Edit the campaign announcement there, then
        use this tab to review or trigger it on a device.
      </p>

      <div className="announcements-container">
        <div className="announcements-list">
          <div className="announcements-list-header">
            <h4>Available Announcements</h4>
            <button type="button" onClick={() => refreshAnnouncements().catch(() => {})}>
              Refresh
            </button>
          </div>

          {loading ? (
            <div className="announcements-empty">Loading campaign announcements…</div>
          ) : announcements.length ? (
            <ul>
              {announcements.map((ann) => (
                <li
                  key={ann.id}
                  className={selectedAnnouncement?.id === ann.id ? 'selected' : ''}
                  onClick={() => {
                    setSelectedAnnouncementId(ann.id);
                    setPlayNowStatus(null);
                  }}
                >
                  <div className="announcement-list-title">{ann.title || `Announcement ${ann.id}`}</div>
                  <div className="announcement-list-meta">
                    {ann.campaign_title || 'Campaign'} {ann.variation_idx != null ? `• Variation ${ann.variation_idx}` : ''}
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="announcements-empty">
              No campaign announcements found. Save an announcement from a campaign in Creative Studio first.
            </div>
          )}
        </div>

        <div className="announcement-details">
          {selectedAnnouncement ? (
            <>
              <h4>{selectedAnnouncement.title || 'Announcement'}</h4>
              <div className="announcement-detail-grid">
                <div className="announcement-detail-card">
                  <span className="announcement-detail-label">Campaign</span>
                  <strong>{selectedAnnouncement.campaign_title || 'Unknown Campaign'}</strong>
                </div>
                <div className="announcement-detail-card">
                  <span className="announcement-detail-label">Language</span>
                  <strong>{getLanguageLabel(selectedAnnouncement.language_code)}</strong>
                </div>
                <div className="announcement-detail-card">
                  <span className="announcement-detail-label">Voice</span>
                  <strong>{getVoiceDisplayName(selectedAnnouncement.voice_name || '', selectedAnnouncement.language_code || '')}</strong>
                </div>
                <div className="announcement-detail-card">
                  <span className="announcement-detail-label">Created</span>
                  <strong>{selectedAnnouncement.created_at ? new Date(selectedAnnouncement.created_at).toLocaleString() : 'Unknown'}</strong>
                </div>
              </div>

              <div className="announcement-body">
                <span className="announcement-detail-label">Announcement Text</span>
                <div className="announcement-copy">{selectedAnnouncement.content || 'No announcement text.'}</div>
              </div>

              <div className="announcement-play-panel">
                <div className="announcement-play-header">
                  <strong>Play Now</strong>
                  <span>Trigger this campaign announcement on one TV or Music Player for runtime testing.</span>
                </div>

                {playableDevices.length > 0 ? (
                  <>
                    <label>
                      Target Device:
                      <select
                        value={playTargetDeviceId}
                        onChange={(e) => setPlayTargetDeviceId(e.target.value)}
                      >
                        <option value="" disabled>
                          -- Select a Device --
                        </option>
                        {playableDevices.map((device) => (
                          <option key={device.id} value={device.id}>
                            {device.name} ({device.type})
                          </option>
                        ))}
                      </select>
                    </label>
                    <button
                      type="button"
                      className="announcement-play-button"
                      onClick={handlePlayAnnouncementNow}
                      disabled={playNowBusy || !selectedAnnouncement?.id || !playTargetDeviceId}
                    >
                      {playNowBusy ? 'Playing…' : 'Play On Device'}
                    </button>
                  </>
                ) : (
                  <div className="announcement-play-empty">
                    Add a TV or Music Player in this area to test announcement interrupts.
                  </div>
                )}

                {playNowStatus && (
                  <div className={`announcement-play-status ${playNowStatus.type}`}>
                    {playNowStatus.message}
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="announcements-empty">
              Select a campaign announcement from the list to inspect it here.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Announcements;
